import React, { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap } from '@momentum/types';
import { SendHorizontal } from 'lucide-react';
import { Input } from '@/shared/components/ui/Input';
import { useTranslation } from 'react-i18next';

interface WizardChatbotTextProps
  extends WizardStepProps<ComponentReturnTypeMap['ChatbotText']> {
  placeholder?: string;
  maxLength?: number;
  showSkipOption?: boolean;
  skipText?: string;
}

export const WizardChatbotText: React.FC<WizardChatbotTextProps> = ({
  value,
  onNext,
  placeholder,
  maxLength,
  showSkipOption = false,
  skipText = 'Skip for now',
}) => {
  const [text, setText] = useState(value || '');
  const { t } = useTranslation('wizard');
  placeholder = placeholder ?? t('debriefQuestions.matchPoint.why.placeholder');

  const handleSubmit = () => {
    if (text.trim()) {
      onNext(text.trim());
    }
  };

  const handleSkip = () => {
    onNext('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 pe-2">
        <Input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder}
          maxLength={maxLength}
          className="flex-1 h-auto px-5 py-3.5 border-0 bg-transparent text-base focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <Button
          onClick={handleSubmit}
          disabled={!text.trim()}
          size="sm"
          className="rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 transition-all duration-200 px-4 py-2 flex-shrink-0"
        >
          <SendHorizontal className="w-4 h-4" />
        </Button>
      </div>

      {showSkipOption && (
        <button
          onClick={handleSkip}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-150"
        >
          {skipText}
        </button>
      )}
    </div>
  );
};
